import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { readItems } from "@directus/sdk";
import classNames from "classnames";
import client from "directus/src/client";
import { getColorClasses } from "utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "ui/components/sheet";
import { Icon } from "components";
import { Button } from "ui/components/button";

// Tag picker: a compact trigger ("3 tags" + clear) that opens a drawer with all
// tags as clickable badges. Same value contract as the comboboxes it replaces:
// multi -> array of tag ids, single -> one tag id (or null).
export default function TagsDrawer({ filter, isMulti, values = [], onValuesChange, placeholder, className }) {
  const [open, setOpen] = useState(false);
  const selected = (isMulti ? values : values.slice(0, 1)).filter(Boolean);

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ["tags-drawer", filter],
    queryFn: () =>
      client.request(readItems("tags", { fields: ["id", "label", "color"], filter, sort: ["label"], limit: -1 })),
    enabled: open,
  });

  // Clicking tags only edits this draft; nothing is applied until "Apply".
  const [draft, setDraft] = useState(selected);
  const openDrawer = () => {
    setDraft(selected);
    setOpen(true);
  };

  const emit = (ids) => onValuesChange?.(isMulti ? ids : ids[0] ?? null);
  const toggle = (id) =>
    setDraft((d) => (d.includes(id) ? d.filter((v) => v !== id) : isMulti ? [...d, id] : [id]));
  const allSelected = tags.length > 0 && tags.every((t) => draft.includes(t.id));
  const apply = () => {
    emit(draft);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={openDrawer}
        className={classNames(
          "flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-background px-3 text-sm",
          className,
        )}
      >
        <span className={selected.length ? "" : "text-muted-foreground"}>
          {selected.length ? `${selected.length} tag${selected.length > 1 ? "s" : ""}` : placeholder}
        </span>
        {selected.length > 0 ? (
          <span
            role="button"
            title="Clear tags"
            onClick={(e) => {
              e.stopPropagation();
              emit([]);
            }}
            className="rounded p-0.5 hover:bg-muted"
          >
            <Icon icon="lucide:x" className="size-4" />
          </span>
        ) : null}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="flex flex-col">
          <SheetHeader>
            <SheetTitle>Tags</SheetTitle>
          </SheetHeader>
          {/* Select all / Deselect all sits on its own row, apart from the tags. */}
          {isMulti && tags.length > 0 && (
            <div className="flex items-center justify-between border-b px-4 pb-3">
              <span className="text-sm text-muted-foreground">{draft.length} selected</span>
              <button
                type="button"
                onClick={() => setDraft(allSelected ? [] : tags.map((t) => t.id))}
                className="cursor-pointer rounded-full border border-dashed px-2.5 py-1 text-xs font-medium hover:bg-muted"
              >
                {allSelected ? "Deselect all" : "Select all"}
              </button>
            </div>
          )}
          <div className="flex flex-1 flex-wrap content-start gap-2 overflow-y-auto px-4 py-3">
            {isLoading && <span className="text-sm text-muted-foreground">Loading tags...</span>}
            {tags.map((tag) => {
              const isOn = draft.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggle(tag.id)}
                  className={classNames(
                    "inline-flex cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium capitalize transition-opacity",
                    getColorClasses(tag.color),
                    isOn ? "ring-1 ring-primary/60" : "opacity-60 hover:opacity-100",
                  )}
                >
                  {isOn && <Icon icon="lucide:check" className="size-3" />}
                  {tag.label}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 border-t p-4">
            <Button variant="outline" onClick={() => {
                // Clears the applied tag filter straight away and closes the drawer.
                setDraft([]);
                emit([]);
                setOpen(false);
              }}
              disabled={draft.length === 0 && selected.length === 0}
            >
              Clear
            </Button>
            <Button onClick={apply}>Apply</Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
